import { useChild } from "@contentstech/stackflow-plugin-omniflow";
import { useActivity } from "@contentstech/stackflow-solid";
import { type ActivityComponentType, useFlow } from "@contentstech/stackflow-solid/future";
import Tooltip from "@corvu/tooltip";
import { Button as RawButton } from "@kobalte/core/button";
import { createMutation, createQuery } from "@tanstack/solid-query";
import { type JSXElement, Show, Suspense, createEffect, createMemo } from "solid-js";
import { Temporal } from "temporal-polyfill";
import { solidQueryClient } from "~/app";
import { promotionEventStatus } from "~/components/PromotionEvent0331";
import { AppBar } from "~/components/ui/appbar";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { useTopAlert } from "~/components/ui/topAlert";
import {
  getPayoutControllerGetPayoutDetailsApiV1Url,
  getPayoutControllerGetPayoutHistoryApiV1Url,
  getPayoutControllerGetPayoutStatusApiV1Url,
  payoutControllerGetPayoutDetailsApiV1,
  payoutControllerGetPayoutStatusApiV1,
  payoutControllerRequestWithdrawalApiV1,
} from "~/lib/api/__generated__/api.schema";
import { environment } from "~/lib/environment";
import { latest } from "~/lib/latest";
import { cn } from "~/lib/utils";
import { PayoutAlertBanner, getCurrentMonthAndLastDay } from "./PayoutBanner";
import { PayoutHistory } from "./payoutHistory";

declare module "@stackflow/config" {
  interface Register {
    PayoutActivity: Record<string, never>;
  }
}

const PayoutActivity: ActivityComponentType<"PayoutActivity"> = () => {
  const actions = useFlow();
  const topAlert = useTopAlert();
  const payoutDetailsQuery = createQuery(() => ({
    queryKey: [getPayoutControllerGetPayoutDetailsApiV1Url()],
    queryFn: async () => payoutControllerGetPayoutDetailsApiV1(),
  }));
  const statusQuery = createQuery(() => ({
    queryKey: [getPayoutControllerGetPayoutStatusApiV1Url()],
    queryFn: async () => payoutControllerGetPayoutStatusApiV1(),
  }));
  const requestWithdrawalMutation = createMutation(() => ({
    mutationFn: async () => payoutControllerRequestWithdrawalApiV1(),
  }));

  const child = useChild();
  const hasChild = createMemo(() => !!child?.());
  const activity = useActivity();
  const latestChild = createMemo<JSXElement>((prev) => child?.()?.render() ?? prev);
  const isPayoutDay = Temporal.Now.plainDateISO("America/Los_Angeles").day >= 25;
  createEffect(() => {
    activity()?.isTop; // trigger the effect on mobile
    hasChild(); // trigger the effect on desktop
  });

  return (
    <div class="flex flex-col env-desktop:gap-20px">
      {isPayoutDay && <PayoutAlertBanner />}
      <AppBar title="Payouts" />
      <div class="relative flex-1 flex flex-col env-mobile:(px-16px mb-[var(--safe-area-inset-bottom)]) env-desktop:(px-32px max-w-1080px gap-20px)">
        <Show when={latest(() => !payoutDetailsQuery.data?.data.item)}>
          <RawButton
            class="flex flex-row rounded-8px cursor-pointer px-16px py-12px gap-12px items-center bg-[linear-gradient(90deg,#DEF54F_0%,#7ED0FF_100%)]"
            onClick={() => {
              actions.push("PayoutDetailsActivity", {});
            }}
          >
            <i class="inline-block i-bp-bank" />
            <div class="flex flex-col gap-2px text-left">
              <p class="prose-sm-b">Add your bank</p>
              <p class="prose-xs">When your payout is ready, it will be sent to this account</p>
            </div>
            <div class="flex-1" />
            <i class="inline-block i-bp-arrow-right" />
          </RawButton>
        </Show>
        <div class="flex flex-col gap-12px env-desktop:(flex-row my-0px) env-mobile:(mt-12px)">
          <Suspense fallback={<Skeleton height={132} />}>
            <div
              class={cn(
                "w-100% flex flex-col items-stretch gap-12px rounded-8px p-16px bg-background-dark text-primary bg-white env-desktop:(max-w-400px)",
                promotionEventStatus.hasPromotionalEvent && "border-1 border-black relative overflow-hidden",
              )}
            >
              <Show when={promotionEventStatus.hasPromotionalEvent}>
                <div class="absolute top-0 right-0 bg-black rounded-bl-8px py-2px px-8px">
                  <p class="prose-2xs text-white">80% revenue share</p>
                </div>
              </Show>
              <div class="flex flex-col">
                <div class="flex flex-col flex-1 gap-8px">
                  <div class="flex gap-6px">
                    <span class="prose-xs">Balance</span>
                    <PayoutInfoTooltip />
                  </div>

                  <p class="prose-xl-b">${(latest(() => statusQuery.data?.data.item.balance) ?? 0).toLocaleString()}</p>
                </div>
                <Button
                  variant="highlighted"
                  size="sm"
                  class={cn("w-100% my-16px", !isPayoutDay && "py-6px!")}
                  disabled={latest(() => statusQuery.data?.data.item.withdrawalStatus.enabled) === false}
                  onClick={async () => {
                    const { status } = await requestWithdrawalMutation.mutateAsync();
                    // TODO: Handle more informative for users
                    if (!status.toString().startsWith("2")) {
                      topAlert.open({
                        variant: "error",
                        children: (
                          <div>
                            <p>Something went wrong! Please contact support</p>
                          </div>
                        ),
                      });
                      return;
                    }
                    solidQueryClient.invalidateQueries({
                      queryKey: [getPayoutControllerGetPayoutStatusApiV1Url()],
                    });
                    solidQueryClient.invalidateQueries({
                      queryKey: [getPayoutControllerGetPayoutHistoryApiV1Url()],
                    });
                  }}
                >
                  <div class={!isPayoutDay ? "leading-0" : ""}>
                    <div class={isPayoutDay ? "prose-sm-b" : "prose-xs-b"}>Withdraw</div>
                    <Show when={!isPayoutDay}>
                      <span class="prose-2xs text-tertiary">
                        Available from {getCurrentMonthAndLastDay().currentMonth} 25 to{" "}
                        {getCurrentMonthAndLastDay().lastDayOfMonth}(PST)
                      </span>
                    </Show>
                  </div>
                </Button>
                <span class="prose-xs text-secondary"> Withdrawals available for balances over $50</span>
              </div>
            </div>
          </Suspense>
        </div>
        <PayoutHistory />
      </div>
      <Show when={child}>
        <Dialog open={hasChild()}>
          <DialogContent class="env-desktop:(max-h-800px overflow-y-auto pt-0)">
            <DialogHeader onClose={() => actions.pop()} class="sticky top-0 pt-20px pb-10px bg-white z-30">
              <DialogTitle class="text-primary">Payout details</DialogTitle>
            </DialogHeader>
            {latestChild()}
          </DialogContent>
        </Dialog>
      </Show>
    </div>
  );
};

const PayoutInfoTooltip = () => {
  return (
    <Tooltip
      placement="bottom"
      openDelay={100}
      closeOnPointerDown={false}
      openOnHover={environment === "desktop"}
      floatingOptions={{
        offset: 4,
        shift: true,
        size: {
          fitViewPort: true,
        },
      }}
    >
      <Tooltip.Anchor class="flex items-center">
        <Tooltip.Trigger class="flex">
          <i class="inline-block i-bp-info text-secondary" />
        </Tooltip.Trigger>
      </Tooltip.Anchor>
      <Tooltip.Portal>
        <Tooltip.Content class="relative rounded-8px bg-[#2F2F2F] z-999 w-280px shadow-[0px_4px_12px_0px_rgba(0,0,0,0.15)] overflow-y-auto env-mobile:(w-180px)">
          <div class="px-12px py-8px text-white prose-xs leading-[1.4] text-center">
            Payout amount may differ slightly from the estimated revenue due to YouTube's settlement and processing
            methods.
          </div>
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip>
  );
};

export default PayoutActivity;
